import React, { useState, useRef, useEffect, useCallback, useMemo } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import debounce from 'lodash.debounce';
import customAxios from '../../../../../api/customAxios';
import { authHeader } from '../../../../../authentication/authTokenService';
import { 
  Box, 
  Typography, 
  TextField, 
  Select, 
  MenuItem, 
  FormControl, 
  InputLabel, 
  FormControlLabel,
  RadioGroup,
  Radio,
  Card,
  CardContent,
  CardHeader,
  Grid,
  CircularProgress,
  IconButton,
  Avatar,
  InputAdornment,
  Container,
  Stack,
  Tooltip,
  FormHelperText,
  FormLabel,
  Autocomplete,
  Alert
} from '@mui/material';
import {
  Close as X,
  AttachMoney as DollarSign,
  Info,
  AutoFixHigh as Wand2
} from '@mui/icons-material';
import { styled } from '@mui/material/styles';
import { pink } from '@mui/material/colors';
import { tooltipClasses } from "@mui/material/Tooltip";

// Import our modular components
import DefectImageUploaderWithGenerate from '../../../components/imageUploader/DefectImageUploaderWithGenerate';
import DefectDatabaseSearch from './DefectDatabaseSearch';
import FormSummary from './sections/FormSummary';
import { fetchSubCategoryElementList } from '../../../api/elementSectionApi';
import { resetSliceState } from '../element-section/elementSectionSlice';
import MultiplePriority from '../MultiplePriority';

// Import backend integration helpers from old form
import { 
  calulateTotal, // Use old form's calculation logic
  convertToDecimal, // For threshold warning calculation
  calculateDistributedCosts, // Multiple Priority cost distribution with exact total match
  setDefaultValues,
  setDefaultRiskTypeValues,
  getRiskTypeDisplayText,
  getDefectTypeDisplayText,
  getPriorityTypeDisplayText,
  getLanguageBasedOption
} from '../defectHelper';

// Import cost element API for fetching suggestions (like old form)
import { fetchCostElementList } from '../../../api/costElementApi';

// Import constants and utilities from old form
import { riskTypeOptions } from '../../../../../utils/constants';
import {
  riskOptions,
  priorityOptions,
  defectTypes,
  others,
  getUnits
} from '../../../../../common/dropdowns';
import {
  getLabel,
  getTranslationVariantText,
  getIntlTranslation,
} from '../../../../../utils/helper';

import enTranslations from '../../../../../lang/en';

// Styled tooltip to match main defect mask
const StyledTooltip = styled(({ className, ...props }) => (
  <Tooltip {...props} classes={{ popper: className }} />
))(({ theme }) => ({
  [`& .${tooltipClasses.tooltip}`]: {
    backgroundColor: '#fff',
    color: '#000',
    maxWidth: 500,
    border: '1px solid #dadde9',
    padding: '16px',
    fontSize: '14px',
    lineHeight: '1.5',
    boxShadow: theme.shadows[1]
  },
}));

// Radio button styling to match main defect mask exactly
const radioButton = {
  "&.Mui-checked": {
    color: pink[600],
  },
};

// Highlighted text component for tooltips
const HighlightedText = ({ color, children }) => (
  <span style={{ color, fontWeight: 'bold' }}>{children}</span>
);

// Warning content component for risk tooltips
const WarningContent = ({ type }) => {
  if (type === "high") {
    return (
      <div>
        {getTranslationVariantText("high-warning-start", "subtitle")}{' '}
        <HighlightedText color="#ff0000">{getTranslationVariantText("health", "subtitle")}</HighlightedText> {getTranslationVariantText("and-or", "subtitle")}{' '}
        <HighlightedText color="#ff0000">{getTranslationVariantText("safety", "subtitle")}</HighlightedText>, {getTranslationVariantText("including", "subtitle")}{' '}
        <HighlightedText color="#ff0000">{getTranslationVariantText("structural-safety", "subtitle")}</HighlightedText>,{' '}
        <HighlightedText color="#ff0000">{getTranslationVariantText("fire-protection", "subtitle")}</HighlightedText> {getTranslationVariantText("and", "subtitle")}{' '}
        <HighlightedText color="#ff0000">{getTranslationVariantText("building-laws", "subtitle")}</HighlightedText> /{' '}
        <HighlightedText color="#000">{getTranslationVariantText("high-warning-end", "subtitle")}</HighlightedText>
      </div>
    );
  } else if (type === "medium") {
    return (
      <div>
        {getTranslationVariantText("medium-warning-start", "subtitle")}{' '}
        <HighlightedText color="#ffa500">{getTranslationVariantText("functionality-usability", "subtitle")}</HighlightedText>{' '}
        {getTranslationVariantText("is", "subtitle")}{' '}
        <HighlightedText color="#ffa500">{getTranslationVariantText("at-risk", "subtitle")}</HighlightedText>, {getTranslationVariantText("as-well-as", "subtitle")}{' '}
        <HighlightedText color="#ffa500">{getTranslationVariantText("overdue-lifecycle", "subtitle")}</HighlightedText>{' '}
        <HighlightedText color="#000">{getTranslationVariantText("medium-warning-end", "subtitle")}</HighlightedText>
      </div>
    );
  } else {
    return (
      <div>
        <HighlightedText color="#008000">{getTranslationVariantText("defects-problems", "subtitle")}</HighlightedText>{' '}
        <HighlightedText color="#000">{getTranslationVariantText("low-warning-middle", "subtitle")}</HighlightedText>{' '}
        <HighlightedText color="#008000">{getTranslationVariantText("future-maintenance", "subtitle")}</HighlightedText>,{' '}
        <HighlightedText color="#000">{getTranslationVariantText("low-warning-end", "subtitle")}</HighlightedText>
      </div>
    );
  }
};

// Risk tooltip content using actual translation text
const getRiskTooltipContent = (riskLevel) => {
  switch (riskLevel.toLowerCase()) {
    case 'high':
      return (
        <Box sx={{ maxWidth: 400 }}>
          <Typography variant="body2">
            {enTranslations["high-warning-start"]} <strong>{enTranslations.health}</strong> {enTranslations["and-or"]} <strong>{enTranslations.safety}</strong>, {enTranslations.including} <strong>{enTranslations["structural-safety"]}</strong>, <strong>{enTranslations["fire-protection"]}</strong> {enTranslations["and-or"]} <strong>{enTranslations["building-laws"]}</strong> / {enTranslations["high-warning-end"]}
          </Typography>
        </Box>
      );
    case 'medium':
      return (
        <Box sx={{ maxWidth: 400 }}>
          <Typography variant="body2">
            {enTranslations["medium-warning-start"]} <strong>{enTranslations["functionality-usability"]}</strong> {enTranslations.is} {enTranslations["at-risk"]}, {enTranslations["as-well-as"]} <strong>{enTranslations["overdue-lifecycle"]}</strong> {enTranslations["medium-warning-end"]}
          </Typography>
        </Box>
      );
    case 'low':
      return (
        <Box sx={{ maxWidth: 400 }}>
          <Typography variant="body2">
            <strong>{enTranslations["defects-problems"]}</strong> {enTranslations["low-warning-middle"]} <strong>{enTranslations["future-maintenance"]}</strong> {enTranslations["low-warning-end"]}
          </Typography>
        </Box>
      );
    default:
      return <Typography variant="body2">Select a risk level for guidance.</Typography>;
  }
};

function EnhancedDefectForm({ values, setFieldValue, errors, touched, subCategory, defectsData = null, onImageUploadData }) { 
  const isMultiplePriority = (v) => !!v && ['multiple','mehrere'].includes(String(v).toLowerCase());
  const multipleSelected = useMemo(() => isMultiplePriority(values.priority || values.priorityType), [values.priority, values.priorityType]);

  // Import for element fetching - moved before getTextLabel to fix initialization error
  const scope = useSelector((state) => state.container.scope);
  const elementState = useSelector((state) => state.tddElementSection);
  const dispatch = useDispatch();

  // Component cleanup on mount to ensure fresh state
  useEffect(() => {
    // Clear any element section state that might be cached
    dispatch(resetSliceState());
    
    // Clear any local state that might cause issues
    setFilteredElementOptions([]);
    setIsElementOther(false);
    
    // Clear any defects database cache that might cause issues
    try {
      // Import defectsLazyLoader if available and clear its cache
      import('../../../../../utils/defectsLazyLoader').then(module => {
        const defectsLazyLoader = module.default;
        if (defectsLazyLoader && typeof defectsLazyLoader.clearCache === 'function') {
          defectsLazyLoader.clearCache();
        }
      }).catch(err => {
        // Silent error handling
      });
    } catch (error) {
      // Silent error handling
    }
    
    return () => {
      // Additional cleanup if needed
    };
  }, [dispatch]);

  // Helper function to get text-only translations for form labels (like old form)
  const getTextLabel = useCallback((key, fallback = '') => {
    try {
      // For cost data and other key labels, use getIntlTranslation like the old form
      const intlTranslationKeys = [
        '_Cost-Data', '_Unit', '_Price-per-Unit', '_Quantity', 
        '_Extra-Cost-(Optional)', '_%-of-the-element-affected', '_Extra-Cost-Comments',
        '_quelle', '_Q&A'
      ];
      
      if (intlTranslationKeys.includes(key)) {
        const result = getIntlTranslation(key);
        // Ensure we always return a string
        return typeof result === 'string' ? result : (fallback || key.replace(/^_/, '').replace(/-/g, ' '));
      }
      
      // Fallback to cleaned key for any remaining cases
      const cleanedKey = key.replace(/^_/, '').replace(/-/g, ' ');
      return fallback || cleanedKey;
    } catch (error) {
      const cleanedKey = key.replace(/^_/, '').replace(/-/g, ' ');
      return fallback || cleanedKey;
    }
  }, []);

  // Safe translation function that always returns a string
  const getSafeTranslation = useCallback((key, fallback = '') => {
    try {
      const result = getIntlTranslation(key);
      return typeof result === 'string' ? result : (fallback || key.replace(/^_/, '').replace(/-/g, ' '));
    } catch (error) {
      return fallback || key.replace(/^_/, '').replace(/-/g, ' ');
    }
  }, []);

  // Radio button options - same as old form
  const riskTypeOptionsArray = riskOptions;
  
  // Filter defectTypes to show only valid options for cost/non-cost defects
  const defectTypeOptions = useMemo(() => {
    // Filter out any invalid options and ensure all have proper structure
    const baseOptions = defectTypes.filter(option => 
      option && option.value && option.title && 
      typeof option.title === 'object'
    );
    
    // Filter options based on cost defect status
    // "Hinweis" should only show for non-cost defects
    return baseOptions.filter(option => {
      if (option.value === "Hinweis") {
        return !values.costDefect; // Only show Hinweis for non-cost defects
      }
      return true; // Show all other options regardless
    });
  }, [values.costDefect]);
  
  const priorityTypeOptions = !values.costDefect
    ? priorityOptions.filter((f) => f.nonCostDefect)
    : priorityOptions;

  // State management
  const [aiParaphrasingDefect, setAiParaphrasingDefect] = useState(false);
  const [aiParaphrasingMeasure, setAiParaphrasingMeasure] = useState(false);
  const [showPriorityDistribution, setShowPriorityDistribution] = useState(false);
  const [filteredElementOptions, setFilteredElementOptions] = useState([]);
  const [isElementOther, setIsElementOther] = useState(false);
  const [rangeData, setRangeData] = useState([30, 70]);
  const [multipleCost, setMultipleCost] = useState({});
  const [apiCallCondition, setApiCallCondition] = useState({
    projectIdent: null,
    ident: null,
    elementName: null,
    defectType: null,
  });
  
  // Optimized text input handlers to prevent typing lag
  const [localDefectDescription, setLocalDefectDescription] = useState('');
  const [localMeasureDescription, setLocalMeasureDescription] = useState('');
  const [localElementName, setLocalElementName] = useState(''); // Add local state for element name
  
  // Consolidated local state for all text fields to prevent typing lag
  const [localTextFields, setLocalTextFields] = useState({
    quelle: '',
    dataRoomIndex: '',
    qaField: '',
    comments: '',
    extraCostComment: ''
  });

  // Helper function to safely convert values to strings  
  const safeStringValue = (value) => {
    if (value === null || value === undefined) return '';
    if (typeof value === 'object') return '';
    return String(value);
  };

  // Create stable row object for DefectImageUploader to prevent infinite API calls
  const stableRowForDefectImages = useMemo(() => ({
    defectIdent: values.defectIdent || null,
    trade: values.elementName || { value: '' },
    type: 'defect_row',
    deleted: false,
    imageCount: 0
  }), [values.defectIdent, values.elementName?.value]);

  // Helper function to update Formik values (optimized for backend compatibility)
  const setFormData = useCallback((updates) => {
    Object.keys(updates).forEach(key => {
      if (key === 'isNonCostDefect') {
        setFieldValue('costDefect', !updates[key]);
      } else if (key === 'inRedFlag') {
        setFieldValue('inRedFlag', updates[key]);
      } else if (key === 'costComment') {
        // Map costComment to both API fields for compatibility
        setFieldValue('extraCostComment', updates[key]);
        setFieldValue('costComment', updates[key]);
      } else if (key === 'priorityType') {
        // Sync priority fields for backend compatibility
        setFieldValue('priorityType', updates[key]);
        setFieldValue('priority', updates[key]);
      } else if (key === 'priority') {
        // Sync priority fields for backend compatibility
        setFieldValue('priority', updates[key]);
        setFieldValue('priorityType', updates[key]);
      } else {
        setFieldValue(key, updates[key]);
      }
    });
  }, [setFieldValue]);

  // Optimized change handlers with backend integration (exactly like old form)
  const handleRiskChange = useCallback((value) => {
    setFieldValue('riskType', value);
    
    // Backend integration: Apply default values based on risk type (like old form)
    const defaults = setDefaultRiskTypeValues(value);
    
    // Enhanced safety check for low risk selection (exactly like old form)
    if (value === riskTypeOptions.LOW) {
      // First check if red flag is checked
      if (values.inRedFlag) {
        // Uncheck it before disabling
        setFieldValue('inRedFlag', false);
      }
    } else {
      // For other risk types, use the default behavior
      setFieldValue('inRedFlag', defaults.isRedFlag);
    }
  }, [setFieldValue, values.inRedFlag]);

  const handleDefectTypeChange = useCallback((value) => {
    // Backend integration: Apply default values based on defect type (exactly like old form)
    const data = setDefaultValues(value);
    setFieldValue('defectType', data.defectType);
    setFieldValue('riskType', data.risk);
    setFieldValue('priority', data.priority);
    setFieldValue('priorityType', data.priority); // Sync both priority fields
    setFieldValue('inRedFlag', data.isRedFlag);
  }, [setFieldValue]);

  const handlePriorityChange = useCallback((value) => {
    // Exactly like old form priority change
    setFieldValue('priority', value);
    setFieldValue('priorityType', value); // Sync both fields
    
    if (value !== null) {
      if (value === 'Multiple') {
        setFieldValue('isMultiple', true);
        setShowPriorityDistribution(true);
        
        // Set default distribution percentages like old form
        const distribution = { 
          shortTerm: 30, 
          mediumTerm: 40, 
          longTerm: 30 
        };
        setFieldValue('percentageShortTermPriority', distribution.shortTerm);
        setFieldValue('percentageMidTermPriority', distribution.mediumTerm);
        setFieldValue('percentageLongTermPriority', distribution.longTerm);
        
        // Set multipleCost object for backend compatibility
        setFieldValue('multipleCost', {
          short: distribution.shortTerm / 100,
          medium: distribution.mediumTerm / 100,
          long: distribution.longTerm / 100
        });
      } else {
        setFieldValue('isMultiple', false);
        setShowPriorityDistribution(false);
        // Clear distribution fields
        setFieldValue('percentageShortTermPriority', 0);
        setFieldValue('percentageMidTermPriority', 0);
        setFieldValue('percentageLongTermPriority', 0);
        setFieldValue('multipleCost', {
          short: 0,
          medium: 0,
          long: 0
        });
      }
    }
  }, [setFieldValue]); // Simplified dependencies

  const handleRedFlagChange = useCallback((checked) => {
    setFieldValue('inRedFlag', checked);
    if (checked) {
      setFieldValue('riskType', 'High');
    }
  }, [setFieldValue]);
  
  // Debounced update functions to prevent lag during typing
  const debouncedDefectDescriptionUpdate = useCallback(
    debounce((value) => {
      setFieldValue('defectDescription', value);
    }, 300),
    [setFieldValue]
  );

  const debouncedMeasureDescriptionUpdate = useCallback(
    debounce((value) => {
      setFieldValue('measureDescription', value);
    }, 300),
    [setFieldValue]
  );

  // Single debounced update function for all text fields
  const debouncedTextFieldUpdate = useCallback(
    debounce((fieldName, value) => {
      switch (fieldName) {
        case 'quelle':
          setFieldValue('quelle', value);
          break;
        case 'dataRoomIndex':
          setFieldValue('dataRoomIndex', value);
          break;
        case 'qaField':
          setFieldValue('qaField', value);
          setFieldValue('questionAndAnswer', value); // Backend compatibility
          break;
        case 'comments':
          setFieldValue('comments', value);
          break;
        case 'extraCostComment':
          setFieldValue('costComment', value);
          setFieldValue('extraCostComment', value);
          break;
      }
    }, 300),
    [setFieldValue]
  );
  
  const handleDefectDescriptionChange = useCallback((e) => {
    const value = e.target.value;
    setLocalDefectDescription(value); // Update local state immediately for responsive UI
    debouncedDefectDescriptionUpdate(value); // Update Formik after delay
  }, [debouncedDefectDescriptionUpdate]);

  const handleMeasureDescriptionChange = useCallback((e) => {
    const value = e.target.value;
    setLocalMeasureDescription(value); // Update local state immediately for responsive UI
    debouncedMeasureDescriptionUpdate(value); // Update Formik after delay
  }, [debouncedMeasureDescriptionUpdate]);

  // Single change handler factory for all text fields
  const createTextFieldHandler = useCallback((fieldName) => (e) => {
    const value = e.target.value;
    // Update local state immediately for responsive UI
    setLocalTextFields(prev => ({ ...prev, [fieldName]: value }));
    // Update Formik after delay
    debouncedTextFieldUpdate(fieldName, value);
  }, [debouncedTextFieldUpdate]);

  // Fallback empty array since ElementTypeHierarchy.js was removed
  const elementOptions = [];

  // Helper function to process elements suggestion list (like original)
  const elementsSuggestionList = useCallback((arr, obj, parent) => {
    let temp = arr.reduce((list, curr) => {
      if (
        !list.find((f) => f.language === curr.language && f.value === curr.value)
      ) {
        list.push({
          ...curr,
          parentIdent: obj.subCategorySecondaryIdent,
          parentName: parent.title,
        });
      }
      return list;
    }, []);
    return temp;
  }, []);

  // Process elements suggestion list similar to original ElementSection
  const processElementsSuggestions = useCallback((obj) => {
    // Try to get name in project language first, then fallback
    const parentName = obj.subCategorySecondaryName.find(
      (f) => f.language === scope.projectLanguageCode
    ) || obj.subCategorySecondaryName.find(
      (f) => f.language === 'en'
    ) || obj.subCategorySecondaryName[0];
    
    let temp = [];
    // Get elements from elementDtoList
    obj.elementDtoList?.forEach((item) => {
      if (item.elementName.length) {
        temp = [...temp, ...item.elementName];
      }
    });
    
    // Add suggestions from elementsSuggestionList
    let defaultElementList = [...temp, ...obj.elementsSuggestionList];
    
    // Add "Others" using the standard array from common dropdowns (like original line 87)
    temp = [...defaultElementList, ...others];
    
    // Process elements through the elementsSuggestionList helper (like original line 98)
    const processedElementsList = elementsSuggestionList(temp, obj, parentName);
    
    // Filter to show only the project language version and map to dropdown format
    const processedElements = processedElementsList.filter(element => {
      // For elements with language property, only include project language
      if (element.language && element.language !== scope.projectLanguageCode) {
        // Check if there's a version in the project language
        const hasProjectLanguageVersion = processedElementsList.some(e => 
          e.value === element.value && e.language === scope.projectLanguageCode
        );
        // If project language version exists, exclude this one
        if (hasProjectLanguageVersion) return false;
      }
      
      return true;
    }).map(element => ({
      label: element.title || element.value,
      value: element.value,
      parentIdent: element.parentIdent,
      isSubElement: true
    }));
    
    return {
      name: parentName?.title || parentName?.value || 'Unknown',
      ident: obj.subCategorySecondaryIdent,
      elementsList: processedElements
    };
  }, [scope.projectLanguageCode, elementsSuggestionList]);

  // Sync local state with Formik values when they change externally (e.g., from AI auto-fill)
  useEffect(() => {
    if (values.defectDescription !== localDefectDescription) {
      setLocalDefectDescription(values.defectDescription || '');
    }
  }, [values.defectDescription]);

  useEffect(() => {
    if (values.measureDescription !== localMeasureDescription) {
      setLocalMeasureDescription(values.measureDescription || '');
    }
  }, [values.measureDescription]);

  // Sync all text fields with Formik values (consolidated for performance)
  useEffect(() => {
    const newLocalState = {
      quelle: values.quelle || '',
      dataRoomIndex: values.dataRoomIndex || '',
      qaField: values.qaField || values.questionAndAnswer || '',
      comments: values.comments || '',
      extraCostComment: values.extraCostComment || values.costComment || ''
    };

    // Only update if there are actual changes to prevent unnecessary re-renders
    setLocalTextFields(prev => {
      const hasChanges = Object.keys(newLocalState).some(key => prev[key] !== newLocalState[key]);
      return hasChanges ? newLocalState : prev;
    });
  }, [values.quelle, values.dataRoomIndex, values.qaField, values.questionAndAnswer, values.comments, values.extraCostComment, values.costComment]);

  useEffect(() => {
    const currentElementName = values.customElement?.title || '';
    if (currentElementName !== localElementName) {
      setLocalElementName(currentElementName);
    }
  }, [values.customElement?.title]);

  // Effect to initialize subCategory information in form values for API compatibility
  useEffect(() => {
    if (subCategory && !values.subCategory) {
      setFieldValue('subCategory', subCategory);
    }
  }, [subCategory, values.subCategory, setFieldValue]);

  // Effect to fetch element data based on subcategory - optimized to prevent unnecessary calls
  useEffect(() => {
    if (subCategory?.ident && scope.projectIdent) {
      dispatch(
        fetchSubCategoryElementList({
          projectIdent: scope.projectIdent,
          ident: subCategory.ident,
        })
      );
    }
  }, [dispatch, scope.projectIdent, subCategory?.ident]); // Only depend on ident, not entire subCategory object

  // Effect to process fetched element data
  useEffect(() => {
    if (
      elementState.suggestionElements.type === 'FETCHED' &&
      elementState.suggestionElements.data
    ) {
      const processedOptions = [];
      
      // Process all categories, following the original ElementSection logic
      const allCategories = elementState.suggestionElements.data.subCategorySecondaryDtoList || [];
      
      // Filter out "Sonstiges"/"Others" categories initially (like original)
      const regularCategories = allCategories.filter((category) => {
        const categoryName = category.subCategorySecondaryName.find(
          (f) => f.language === scope.projectLanguageCode
        ) || category.subCategorySecondaryName.find(
          (f) => f.language === 'en'
        ) || category.subCategorySecondaryName[0];
        
        return categoryName && categoryName.value !== 'Sonstiges' && categoryName.title !== 'Others';
      });

      // Process each regular category
      regularCategories.forEach((category) => {
        const processed = processElementsSuggestions(category);
        
        // Add category header
        processedOptions.push({
          label: processed.name,
          value: processed.name,
          isHeader: true,
          disabled: true
        });
        
        // Add all elements for this category (regular elements + Others)
        // Note: processElementsSuggestions already includes "Others" like in the original
        processed.elementsList.forEach(element => {
          processedOptions.push({
            label: `  ${element.label}`, // Indent sub-elements
            value: element.value,
            isHeader: false,
            parentIdent: element.parentIdent
          });
        });
      });

      // If no regular categories found, add "Sonstiges"/"Others" as fallback (like original)
      if (regularCategories.length === 0) {
        const sonstigesCategory = allCategories.find((category) => {
          const categoryName = category.subCategorySecondaryName.find(
            (f) => f.language === scope.projectLanguageCode
          ) || category.subCategorySecondaryName.find(
            (f) => f.language === 'en'
          ) || category.subCategorySecondaryName[0];
          
          return categoryName && (categoryName.value === 'Sonstiges' || categoryName.title === 'Others');
        });

        if (sonstigesCategory) {
          const processed = processElementsSuggestions(sonstigesCategory);
          
          processedOptions.push({
            label: processed.name,
            value: processed.name,
            isHeader: true,
            disabled: true
          });
          
          processed.elementsList.forEach(element => {
            processedOptions.push({
              label: `  ${element.label}`,
              value: element.value,
              isHeader: false,
              parentIdent: element.parentIdent
            });
          });
        }
      }

      setFilteredElementOptions(processedOptions);
      dispatch(resetSliceState());
    }
  }, [elementState.suggestionElements, processElementsSuggestions, dispatch, scope.projectLanguageCode]);

  // Memoize element options to prevent unnecessary re-renders - optimized
  const memoizedElementOptions = useMemo(() => {
    if (filteredElementOptions.length > 0) {
      return filteredElementOptions;
    }
    // Fallback to static options if no dynamic data available
    return elementOptions;
  }, [filteredElementOptions, elementOptions]);


  // Effect to initialize isElementOther state based on current form values (backend compatible)
  useEffect(() => {
    // Handle elementType vs elementName field mapping (Enhanced vs Original form)
    let currentElementTypeValue = '';
    
    // Priority: elementType (Enhanced form) -> elementName (Original form)
    if (values.elementType) {
      if (typeof values.elementType === 'object' && values.elementType !== null) {
        const stringValue = values.elementType.value || values.elementType.title || values.elementType.label || '';
        setFieldValue('elementType', stringValue);
        currentElementTypeValue = stringValue;
      } else if (typeof values.elementType === 'string') {
        currentElementTypeValue = values.elementType;
      }
    } else if (values.elementName) {
      // Handle original form structure where element is stored in elementName
      if (typeof values.elementName === 'object' && values.elementName !== null) {
        const stringValue = values.elementName.value || values.elementName.title || values.elementName.label || '';
        // Debug logging temporarily removed to avoid React rendering errors
        // Set both fields for compatibility
        setFieldValue('elementType', stringValue);
        currentElementTypeValue = stringValue;
      } else if (typeof values.elementName === 'string') {
        // Debug logging temporarily removed
        setFieldValue('elementType', values.elementName);
        currentElementTypeValue = values.elementName;
      }
    }
    
    const isOthers = currentElementTypeValue === 'Sonstiges' || currentElementTypeValue === 'Others';
    
    // Only update if value actually changed to prevent unnecessary re-renders
    if (isOthers !== isElementOther) {
      setIsElementOther(isOthers);
    }

    
    // Backend compatibility: Ensure isElementOther field is set only if changed
    if (isOthers !== values.isElementOther) {
      setFieldValue('isElementOther', isOthers);
    }

    
    // Find exact match in the element options
    const foundOption = memoizedElementOptions.find(option => option.value === currentElementTypeValue);
    
    if(foundOption){
      setFieldValue('elementName', {
          value: foundOption.value,
          title: foundOption.label,
          parentIdent: foundOption.parentIdent || subCategory?.ident || ''
        });
    }
  }, [values.elementType, values.isElementOther, setFieldValue]); // Removed isElementOther to prevent circular updates


  // Effect to prepare API call condition for cost element suggestions (like old form)
  // This matches DefectSection.js lines 140-158
  // MUST be AFTER memoizedElementOptions definition
  useEffect(() => {
    // Get element name and parent ident
    let elementNameValue = '';
    let parentIdentValue = '';
    
    if (values.elementName && typeof values.elementName === 'object' && values.elementName.value) {
      elementNameValue = values.elementName.value;
      parentIdentValue = values.elementName.parentIdent || '';
    } else if (values.elementType) {
      elementNameValue = values.elementType;
      // Find parentIdent from the selected option
      const selectedOption = memoizedElementOptions.find(opt => opt.value === values.elementType);
      parentIdentValue = selectedOption?.parentIdent || subCategory?.ident || '';
    }
    
    if (elementNameValue && values.defectType && scope.projectIdent) {
      setApiCallCondition({
        projectIdent: scope.projectIdent,
        ident: parentIdentValue,
        elementName: elementNameValue,
        defectType: values.defectType,
      });
    } else {
      // Clear API call condition if requirements not met
      setApiCallCondition({
        projectIdent: null,
        ident: null,
        elementName: null,
        defectType: null,
      });
    }
  }, [scope.projectIdent, values.defectType, values.elementName, values.elementType, subCategory?.ident, memoizedElementOptions]);

  // Effect to call cost element API when conditions are met (like old form)
  // This matches DefectSection.js lines 160-171
  useEffect(() => {
    if (apiCallCondition.defectType && apiCallCondition.elementName && apiCallCondition.projectIdent && apiCallCondition.ident) {
      dispatch(
        fetchCostElementList({
          projectIdent: apiCallCondition.projectIdent,
          ident: apiCallCondition.ident,
          name: apiCallCondition.elementName,
          defectType: apiCallCondition.defectType,
        })
      );
    }
  }, [apiCallCondition, dispatch]);

  // Effect to sync range data with form values for priority distribution
  useEffect(() => {
    if (multipleSelected) {
      // Use ?? instead of || to preserve 0 values (0 is valid, only fallback for null/undefined)
      const shortPct = values.percentageShortTermPriority ?? 30;
      const mediumPct = values.percentageMidTermPriority ?? 40;
      const longPct = values.percentageLongTermPriority ?? 30;
      
      setMultipleCost({
        short: shortPct / 100,
        medium: mediumPct / 100,
        long: longPct / 100,
      });
      
      setRangeData([
        Number(shortPct),
        Number(shortPct + mediumPct),
      ]);
    }
  }, [
    multipleSelected,
    values.percentageShortTermPriority,
    values.percentageMidTermPriority,
    values.percentageLongTermPriority,
  ]);

  // Effect to set measure description to dash when "Hinweis" is selected
  useEffect(() => {
    if (values.defectType === "Hinweis" && values.measureDescription !== "-") {
      setFieldValue("measureDescription", "-");
      setLocalMeasureDescription("-");
    }
  }, [values.defectType, setFieldValue]);

  const handleTextChange = useCallback((name, val) => {
    setFieldValue(name, val);
  }, []);
  
  const debouncedTextChangeHandler = useMemo(
    () => debounce(handleTextChange, 200),
    [handleTextChange]
  );

  // Helper function to get element type display label
  const getElementTypeLabel = useCallback((elementType) => {
    if (!elementType) return 'Not selected';
    
    const option = memoizedElementOptions.find(opt => opt.value === elementType);
    return option ? option.label : elementType;
  }, [memoizedElementOptions]);

  // Helper function to safely get element type value from form data
  const getElementTypeValue = useCallback((elementType) => {
    if (!elementType) return '';
    if (typeof elementType === 'string') return elementType;
    if (typeof elementType === 'object' && elementType !== null) {
      return elementType.value || elementType.title || elementType.label || '';
    }
    return String(elementType);
  }, []);

  // AI Enhancement integration with OpenAI backend endpoint
  const paraphraseText = async (text) => {
    try {
      const encodedText = encodeURIComponent(text);
      
      const response = await customAxios.post(`/openai/paraphrase?description=${encodedText}`, {}, {
        headers: {
          'Content-Type': 'application/json',
          'Authorization': authHeader()
        },
        timeout: 30000 // 30 second timeout for AI processing
      });
      
      // Handle different possible response structures
      const paraphrasedText = response.data?.paraphrasedText || 
                             response.data?.result || 
                             response.data?.data || 
                             response.data?.text || 
                             response.data?.content ||
                             response.data?.enhanced ||
                             response.data?.paraphrased ||
                             text; // Fallback to original if no paraphrased text found
      
      return paraphrasedText;
    } catch (error) {
      // Fallback to original text if API fails
      return text;
    }
  };

  // AI Enhancement
  const handleAIParaphrase = async (field) => {
    const text = field === 'defect' ? localDefectDescription : localMeasureDescription;
    if (!text || !text.trim()) {
      return;
    }
    
    if (field === 'defect') {
      setAiParaphrasingDefect(true);
    } else {
      setAiParaphrasingMeasure(true);
    }
    
    try {
      const enhanced = await paraphraseText(text.trim());
      
      // Update both local state and Formik field with the enhanced text
      if (field === 'defect') {
        setLocalDefectDescription(enhanced); // Update local state immediately
        setFieldValue('defectDescription', enhanced); // Update Formik
      } else {
        setLocalMeasureDescription(enhanced); // Update local state immediately
        setFieldValue('measureDescription', enhanced); // Update Formik
      }
    } catch (error) {
      // Keep original text if error occurs
    } finally {
      if (field === 'defect') {
        setAiParaphrasingDefect(false);
      } else {
        setAiParaphrasingMeasure(false);
      }
    }
  };

  // Form submission - Remove this function as it will be handled by parent CreateDefect component
  // The form submission is handled by the parent component through Formik

  return (
    <>
      <Container 
        maxWidth="xl" 
        sx={{ 
          py: 2,
          overflow: 'visible',
          width: '100%'
        }}
      >
        <Grid container spacing={3} sx={{ overflow: 'visible' }}>
          {/* Left Column - Defect Information (Wider when non-cost, split when cost defect) */}
          <Grid item xs={12} md={values.costDefect ? 8 : 12}>
            <Card elevation={2} sx={{ borderRadius: 2, height: 'auto' }}>
              <CardHeader 
                title={
                  <Typography variant="h6" sx={{ fontWeight: 'bold', fontSize: '1.1rem' }}>
                    {getIntlTranslation("_Defect-Information")}
                  </Typography>
                }
                avatar={<Avatar sx={{ bgcolor: 'primary.main', width: 32, height: 32 }}><Info /></Avatar>}
                sx={{ pb: 1 }}
              />
              <CardContent sx={{ pt: 1, pb: 2, px: 2 }}>
                <Stack spacing={2.5}>
                  {/* Element Type Section - Aligned with Element Name */}
                  <Box sx={{ pl: 0 }}>
                    <Grid container spacing={2} alignItems="flex-start">
                      <Grid item xs={12} md={6}>
                        <Autocomplete
                          size="small"
                          options={memoizedElementOptions}
                          selectOnFocus
                          clearOnBlur
                          handleHomeEndKeys
                          value={(() => {
                            // Handle both string and object values for elementType
                            // Priority: elementType (Enhanced form) -> elementName.value (Original form)
                            let elementTypeValue = '';

                            if (values.elementType && typeof values.elementType === 'string') {
                              elementTypeValue = values.elementType;
                            } else if (typeof values.elementType === 'object' && values.elementType !== null) {
                              elementTypeValue = values.elementType.value || values.elementType.title || values.elementType.label || '';
                            } else if (values.elementName) {
                              // Check elementName (original form structure)
                              if (typeof values.elementName === 'string') {
                                elementTypeValue = values.elementName;
                              } else if (typeof values.elementName === 'object' && values.elementName !== null) {
                                elementTypeValue = values.elementName.value || values.elementName.title || values.elementName.label || '';
                              }
                            }

                            
                            // Find exact match in the element options
                            const foundOption = memoizedElementOptions.find(option => option.value === elementTypeValue);
                            
                           

                            // if(foundOption){
                            //   setFieldValue('elementName', {
                            //       value: foundOption.value,
                            //       title: foundOption.label,
                            //       parentIdent: foundOption.parentIdent || subCategory?.ident || ''
                            //     });
                            // }

                            // 🔧 FIX: Handle AI-generated element types that don't match dropdown
                            if (elementTypeValue && !foundOption) {
                              // Check if this is an AI-generated value
                              const isAIGenerated = values.aiGeneratedElementType === elementTypeValue;
                              
                              if (isAIGenerated) {
                                const subCategory = memoizedElementOptions.find(option => {
                                  return option.label?.trim()?.toLowerCase() === values.aiGeneratedElementType?.trim()?.toLowerCase();
                                });
                                if (subCategory) {
                                  // If subcategory exists, use it and return early (no need to set to "Others")
                                  setTimeout(() => {
                                    setFieldValue('elementType', subCategory.value);
                                    setFieldValue('isElementOther', false);
                                    setFieldValue('elementName', {
                                      value: subCategory.value,
                                      title: subCategory.label,
                                      parentIdent: subCategory.parentIdent || ''
                                    });
                                    // Clear the AI flag
                                    setFieldValue('aiGeneratedElementType', null);
                                  }, 0);
                                  
                                  return subCategory;
                                }

                                // Only if no subcategory match found, then find "Others"/"Sonstiges" option
                                const othersOption = memoizedElementOptions.find(option => 
                                  (option.value === 'Sonstiges' || option.value === 'Others') && !option.isHeader
                                );

                                
                                
                                if (othersOption) {
                                  // Auto-select "Others" and set the AI value as custom element name
                                  setTimeout(() => {
                                    setFieldValue('elementType', othersOption.value);
                                    setFieldValue('isElementOther', true);
                                    
                                    // Set elementName to the "Others" object (like old form)
                                    const otherElement = others.find((f) => f.language === scope.projectLanguageCode);
                                    setFieldValue('elementName', {
                                      ...otherElement,
                                      parentIdent: othersOption.parentIdent || ''
                                    });
                                    
                                    // Set customElement with the AI-generated value
                                    setFieldValue('customElement', {
                                      value: elementTypeValue,
                                      title: elementTypeValue,
                                      parentIdent: othersOption.parentIdent || '',
                                    });
                                    
                                    // Clear the AI flag
                                    setFieldValue('aiGeneratedElementType', null);
                                  }, 0);
                                  
                                  return othersOption;
                                }
                              } else {
                                // Try to find a partial match for non-AI values (existing behavior)
                                const partialMatch = memoizedElementOptions.find(option => 
                                  option.label?.toLowerCase().includes(elementTypeValue.toLowerCase()) ||
                                  option.value?.toLowerCase().includes(elementTypeValue.toLowerCase()) ||
                                  elementTypeValue.toLowerCase().includes(option.label?.toLowerCase()) ||
                                  elementTypeValue.toLowerCase().includes(option.value?.toLowerCase())
                                );
                                
                                if (partialMatch) {
                                  // Update the form value to the correct dropdown value
                                  setTimeout(() => {
                                    setFieldValue('elementType', partialMatch.value);
                                  }, 0);
                                  
                                  return partialMatch;
                                }
                              }
                            }
                            
                            return foundOption || null;
                          })()}
                          onChange={(event, newValue) => {
                            // Fix: Handle null/undefined newValue properly
                            let selectedValue = '';

                            if (newValue && typeof newValue === 'object' && newValue.value) {
                              selectedValue = newValue.value;
                            } else if (newValue && typeof newValue === 'string') {
                              selectedValue = newValue;
                            }

                            if(!selectedValue) {
                              return;
                            }

                            // If newValue is null/undefined, selectedValue stays empty string
                            
                            // Ensure we always set a string value, never an object
                            setFieldValue('elementType', newValue.value || newValue.title || newValue);
                            
                            // 🔧 FIX: Store elementName with correct parentIdent from newValue (the selected option)
                            setFieldValue('elementName', {
                              value: selectedValue,
                              title: selectedValue,
                              parentIdent: newValue?.parentIdent || subCategory?.ident || ''
                            });
                            
                            // Check if "Others" or "Sonstiges" is selected
                            const isOthersSelected = selectedValue === 'Sonstiges' || selectedValue === 'Others';
                            
                            setIsElementOther(isOthersSelected);
                            setFieldValue('isElementOther', isOthersSelected);
                            
                            if (isOthersSelected) {
                              // For "Others" elements, set customElement with parentIdent
                              setFieldValue('customElement', {
                                title: newValue?.parentName,
                                label: newValue?.parentName,
                                parentIdent: newValue?.parentIdent || subCategory?.ident || '',
                              });
                              
                              // Debug logging temporarily removed to avoid React rendering errors
                            } else {
                              // For regular elements, customElement is not needed but keep for compatibility
                              setFieldValue('customElement', {
                                value: '',
                                title: '',
                                parentIdent: newValue?.parentIdent || subCategory?.ident || '',
                              });
                              setIsElementOther(false);
                              setFieldValue('isElementOther', false);
                            }
                          }}
                          getOptionLabel={(option) => {
                            if (typeof option === 'string') return option;
                            return option.label || option.value || '';
                          }}
                          getOptionDisabled={(option) => option.disabled || false}
                          isOptionEqualToValue={(option, value) => {
                            if (typeof option === 'string' && typeof value === 'string') return option === value;
                            if (typeof option === 'object' && typeof value === 'string') return option.value === value;
                            if (typeof option === 'string' && typeof value === 'object') return option === value.value;
                            return option.value === value.value;
                          }}
                          filterOptions={(options, { inputValue }) => {
                            if (!inputValue) return options;
                            
                            // Simple search - just filter by label text, keep original structure
                            return options.filter(option => {
                              // Always include headers for structure
                              if (option.isHeader) return true;
                              // Filter sub-elements by search text
                              return option.label.toLowerCase().includes(inputValue.toLowerCase());
                            });
                          }}
                          renderOption={(props, option) => (
                            <li 
                              {...props} 
                              key={`${option.value}-${option.isHeader ? 'header' : 'option'}`}
                              style={{
                                fontWeight: option.isHeader ? 'bold' : 'normal',
                                backgroundColor: option.isHeader ? '#f5f5f5' : 'white',
                                paddingLeft: option.isHeader ? 8 : 24,
                                cursor: option.isHeader ? 'default' : 'pointer',
                                pointerEvents: option.isHeader ? 'none' : 'auto'
                              }}
                            >
                              {option.label}
                            </li>
                          )}
                          renderInput={(params) => (
                            <TextField
                              {...params}
                              label="Element Type *"
                              variant="outlined"
                              size="small"
                              placeholder={getIntlTranslation('_Choose-Element-Type')}
                              sx={{
                                '& .MuiOutlinedInput-root': {
                                  height: '40px',
                                  '&.Mui-focused fieldset': {
                                    borderColor: '#1976d2',
                                  },
                                },
                              }}
                              error={touched.elementType && errors.elementType}
                              helperText={touched.elementType && errors.elementType}
                            />
                          )}
                          sx={{ width: '100%' }}
                          blurOnSelect
                          noOptionsText="No matching elements found"
                          openOnFocus
                          disablePortal
                          componentsProps={{
                            popper: {
                              sx: {
                                zIndex: 1300, // Ensure dropdown appears below modal (modal is typically 1300-1400)
                              },
                              modifiers: [
                                {
                                  name: 'flip',
                                  enabled: true,
                                  options: {
                                    altBoundary: true,
                                    rootBoundary: 'viewport',
                                    padding: 8,
                                  },
                                },
                                {
                                  name: 'preventOverflow',
                                  enabled: true,
                                  options: {
                                    altAxis: true,
                                    altBoundary: true,
                                    tether: true,
                                    rootBoundary: 'viewport',
                                    padding: 8,
                                  },
                                },
                              ],
                            },
                          }}
                        />
                      </Grid>

                      {/* Element Name field - Shows when "Others" is selected, positioned on the right */}
                      {isElementOther && (
                        <Grid item xs={12} md={6}>
                          <TextField
                            size="small"
                            fullWidth
                            label={getSafeTranslation("_Element-Name") + " *"}
                            value={localElementName}
                            onChange={(e) => {
                              const customName = e.target.value;
                              setLocalElementName(customName); // Update local state immediately for responsive UI
                              debouncedTextChangeHandler('customElement', {
                                ...values.customElement,
                                value: customName,
                                title: customName,
                              })
                            }}
                            placeholder={getSafeTranslation("_Element-Name")}
                            InputProps={{
                              endAdornment: (
                                <InputAdornment position="end">
                                  <IconButton
                                    aria-label="clear element name"
                                    onClick={() => {
                                      setLocalElementName(''); // Clear local state immediately
                                      setFieldValue('customElement', {
                                        value: '',
                                        title: '',
                                        parentIdent: subCategory?.ident || '',
                                      });
                                    }}
                                    edge="end"
                                    size="small"
                                    sx={{ 
                                      visibility: localElementName ? 'visible' : 'hidden'
                                    }}
                                  >
                                    <X fontSize="small" />
                                  </IconButton>
                                </InputAdornment>
                              ),
                            }}
                            sx={{
                              '& .MuiOutlinedInput-root': {
                                height: '40px',
                              },
                            }}
                            error={touched.elementName && !!errors.elementName}
                            helperText={touched.elementName && errors.elementName ? (typeof errors.elementName === 'string' ? errors.elementName : 'Please enter a valid element name') : ''}
                          />
                        </Grid>
                      )}
                    </Grid>
                  </Box>

                  {/* Defects Database Search Integration - Positioned after Element Type */}
                  <DefectDatabaseSearch
                    setFieldValue={setFieldValue}
                    elementName={(() => {
                      if (isElementOther) {
                        return typeof values.elementName === 'object' && values.elementName !== null 
                          ? (values.elementName.value || values.elementName.title || '') 
                          : (values.elementName || '');
                      } else {
                        return typeof values.elementType === 'object' && values.elementType !== null 
                          ? (values.elementType.value || values.elementType.title || '') 
                          : (values.elementType || '');
                      }
                    })()}
                    disabled={false} // Always show the search component
                    onDefectSelected={(defect) => {
                      // DefectDatabaseSearch already calls applyDefectDatabaseToForm with proper mapping
                      // No need to manually set fields here as it's already handled
                    }}
                  />

                  {/* Radio Button Groups - After element fields */}
                  <Box sx={{ mt: 1 }}>
                    {/* Risk Level */}
                    <Box sx={{ mb: 1.5 }}>
                      <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                        <FormLabel component="div" sx={{ color: '#00000099', fontSize: '0.875rem', fontWeight: 'normal' }}>
                          {getLabel('_Choose-Risk-Type')}
                        </FormLabel>
                        <StyledTooltip title={getTranslationVariantText("Please Hover on Risks", "subtitile")} placement="top" arrow>
                          <IconButton size="small" sx={{ ml: 0.5, p: 0.5 }}>
                            <Info fontSize="small" />
                          </IconButton>
                        </StyledTooltip>
                      </Box>
                      <RadioGroup row
                        value={safeStringValue(values.riskType)}
                        onChange={(e) => handleRiskChange(e.target.value)}
                        sx={{ gap: 0.25 }}
                      >
                        {riskTypeOptionsArray.map((risk) => {
                          const warningType =
                            risk.value === "niedrig"
                              ? "low"
                              : risk.value === "mittel"
                              ? "medium"
                              : "high";

                          return (
                            <StyledTooltip
                              key={risk.value}
                              title={<WarningContent type={warningType} />}
                              placement="top"
                              arrow
                            >
                              <FormControlLabel
                                value={risk.value}
                                control={<Radio sx={radioButton} name="riskType" size="small" />}
                                label={
                                  <Typography variant="body2" sx={{ ml: 0.5 }}>
                                    {getLanguageBasedOption(risk, scope.projectLanguageCode)}
                                  </Typography>
                                }
                                labelPlacement="end"
                              />
                            </StyledTooltip>
                          );
                        })}
                      </RadioGroup>
                    </Box>

                    {/* Defect Type */}
                    <Box sx={{ mb: 1.5 }}>
                      <FormLabel
                        component="div"
                        sx={{
                          mb: 1,
                          color: touched.defectType && errors.defectType ? "#f44336" : "#00000099",
                          fontWeight: 'normal',
                          fontSize: '0.875rem'
                        }}
                        error={touched.defectType && errors.defectType}
                      >
                        {getLabel('_Choose-a-Defect-Type')}
                        <FormHelperText
                          error={true}
                          variant="standard"
                          sx={{ color: '#f44336' }}
                        >
                          {touched.defectType && errors.defectType}
                        </FormHelperText>
                      </FormLabel>
                      <RadioGroup
                        row
                        value={safeStringValue(values.defectType)}
                        onChange={(e) => handleDefectTypeChange(e.target.value)}
                        sx={{ gap: 0.25, flexWrap: 'wrap' }}
                      >
                        {defectTypeOptions.map((type) => (
                          <FormControlLabel
                            key={type.value}
                            value={type.value}
                            control={<Radio sx={radioButton} name="defectType" size="small" />}
                            label={
                              <Typography variant="body2" sx={{ ml: 0.5 }}>
                                {getLanguageBasedOption(type, scope.projectLanguageCode)}
                              </Typography>
                            }
                            labelPlacement="end"
                          />
                        ))}
                      </RadioGroup>
                    </Box>

                    {/* Priority Type */}
                    <Box sx={{ mb: 1 }}>
                      <FormLabel
                        component="div"
                        sx={{
                          mb: 1,
                          color: touched.priority && errors.priority ? "#f44336" : "#00000099",
                          fontWeight: 'normal',
                          fontSize: '0.875rem'
                        }}
                        error={touched.priority && errors.priority}
                      >
                        {getLabel('_Choose-a-Priority-Type')}
                        <FormHelperText
                          error={true}
                          variant="standard"
                          sx={{ color: '#f44336' }}
                        >
                          {touched.priority && errors.priority}
                        </FormHelperText>
                      </FormLabel>
                      <RadioGroup
                        row
                        value={safeStringValue(values.priority)}
                        onChange={(e) => handlePriorityChange(e.target.value)}
                        sx={{ gap: 0.25 }}
                      >
                        {priorityTypeOptions.map((priority) => (
                          <FormControlLabel
                            key={priority.value}
                            value={priority.value}
                            control={<Radio sx={radioButton} name="priority" size="small" />}
                            label={
                              <Typography variant="body2" sx={{ ml: 0.5 }}>
                                {getLanguageBasedOption(priority, scope.projectLanguageCode)}
                              </Typography>
                            }
                            labelPlacement="end"
                          />
                        ))}
                      </RadioGroup>
                    </Box>
                  </Box>

                  {/* Main Description Fields - Aligned with radio groups */}
                  <Box sx={{ pl: 0 }}>
                    <Grid container spacing={2}>
                      <Grid item xs={12} md={6}>
                        <TextField
                          fullWidth
                          multiline
                          rows={8}
                          size="small"
                          label={getLabel("_Defect-Description")}
                          value={localDefectDescription}
                          onChange={handleDefectDescriptionChange}
                          placeholder="Describe the defect in detail..."
                          error={touched.defectDescription && errors.defectDescription}
                          helperText={touched.defectDescription && errors.defectDescription}
                          InputProps={{
                            endAdornment: (
                              <InputAdornment position="end">
                                <IconButton
                                  onClick={() => handleAIParaphrase('defect')}
                                  disabled={aiParaphrasingDefect || !localDefectDescription}
                                  size="small"
                                >
                                  {aiParaphrasingDefect ? <CircularProgress size={16} /> : <Wand2 />}
                                </IconButton>
                              </InputAdornment>
                            )
                          }}
                          sx={{
                            '& .MuiOutlinedInput-root': {
                              backgroundColor: '#fafafa',
                              '&:hover': {
                                backgroundColor: '#f5f5f5',
                              },
                              '&.Mui-focused': {
                                backgroundColor: 'white',
                              },
                            },
                          }}
                        />
                      </Grid>

                      <Grid item xs={12} md={6}>
                        <TextField
                          fullWidth
                          multiline
                          rows={8}
                          size="small"
                          label={getLabel("_Measure-Description")}
                          value={localMeasureDescription}
                          onChange={handleMeasureDescriptionChange}
                          placeholder="Describe the corrective measures..."
                          error={touched.measureDescription && errors.measureDescription}
                          helperText={touched.measureDescription && errors.measureDescription}
                          disabled={values.defectType === "Hinweis"}
                          InputProps={{
                            endAdornment: (
                              <InputAdornment position="end">
                                <IconButton
                                  onClick={() => handleAIParaphrase('measure')}
                                  disabled={aiParaphrasingMeasure || !localMeasureDescription || values.defectType === "Hinweis"}
                                  size="small"
                                >
                                  {aiParaphrasingMeasure ? <CircularProgress size={16} /> : <Wand2 />}
                                </IconButton>
                              </InputAdornment>
                            )
                          }}
                          sx={{
                            '& .MuiOutlinedInput-root': {
                              backgroundColor: '#fafafa',
                              '&:hover': {
                                backgroundColor: '#f5f5f5',
                              },
                              '&.Mui-focused': {
                                backgroundColor: 'white',
                              },
                            },
                          }}
                        />
                      </Grid>
                    </Grid>

                    {/* Additional Information Fields - With better spacing */}
                    <Box sx={{ mt: 2 }}>
                      <Typography variant="subtitle2" sx={{ mb: 1.5, color: 'text.secondary', fontWeight: 500 }}>
                        {getSafeTranslation("_Additional-Information")}
                      </Typography>
                      <Grid container spacing={2}>
                        <Grid item xs={12} md={4}>
                          <TextField
                            fullWidth
                            multiline
                            rows={2}
                            size="small"
                            label="Quelle"
                            value={localTextFields.quelle}
                            onChange={createTextFieldHandler('quelle')}
                            placeholder="Source information..."
                            error={touched.quelle && errors.quelle}
                            helperText={touched.quelle && errors.quelle}
                          />
                        </Grid>
                        <Grid item xs={12} md={4}>
                          <TextField
                            fullWidth
                            multiline
                            rows={2}
                            size="small"
                            label={getTextLabel("_Data-Room-Index")}
                            value={localTextFields.dataRoomIndex}
                            onChange={createTextFieldHandler('dataRoomIndex')}
                            placeholder="Data room index..."
                            error={touched.dataRoomIndex && errors.dataRoomIndex}
                            helperText={touched.dataRoomIndex && errors.dataRoomIndex}
                          />
                        </Grid>
                        <Grid item xs={12} md={4}>
                          <TextField
                            fullWidth
                            multiline
                            rows={2}
                            size="small"
                            label={getTextLabel("_Q&A")}
                            value={localTextFields.qaField}
                            onChange={createTextFieldHandler('qaField')}
                            placeholder="Q&A information..."
                            error={touched.qaField && errors.qaField}
                            helperText={touched.qaField && errors.qaField}
                          />
                        </Grid>
                      </Grid>
                      
                      {/* Comments Field - Full width like old form */}
                      <Box sx={{ mt: 2 }}>
                        <TextField
                          fullWidth
                          multiline
                          maxRows={10}
                          size="small"
                          label={getIntlTranslation("_Comments")}
                          value={localTextFields.comments}
                          onChange={createTextFieldHandler('comments')}
                          placeholder="Additional comments..."
                          error={touched.comments && errors.comments}
                          helperText={touched.comments && errors.comments}
                          sx={{
                            '& .MuiOutlinedInput-root': {
                              backgroundColor: '#fafafa',
                              '&:hover': {
                                backgroundColor: '#f5f5f5',
                              },
                              '&.Mui-focused': {
                                backgroundColor: 'white',
                              },
                            },
                          }}
                        />
                      </Box>
                    </Box>
                  </Box>



                </Stack>
              </CardContent>
            </Card>
          </Grid>

          {/* Right Column - Cost Analysis (Only show when cost defect) */}
          {values.costDefect && (
            <Grid item xs={12} md={4}>
              <Card elevation={2} sx={{ borderRadius: 2, height: 'fit-content', position: 'sticky', top: 20 }}>
                <CardHeader 
                  title={
                    <Typography variant="h6" sx={{ fontWeight: 'bold', fontSize: '1.1rem' }}>
                      {getIntlTranslation("_Cost-Data")}
                    </Typography>
                  }
                  avatar={<Avatar sx={{ bgcolor: 'warning.main', width: 32, height: 32 }}><DollarSign /></Avatar>}
                  sx={{ pb: 1 }}
                />
                <CardContent sx={{ pt: 1, px: 1.5 }}>
                  <Stack spacing={2}>
                    <Grid container spacing={1.5} sx={{ px: 0.5 }}>
                      <Grid item xs={6}>
                        <TextField
                          fullWidth
                          size="small"
                          label={getLabel("_Price-per-Unit")}
                          type="number"
                          value={safeStringValue(values.pricePerUnit)}
                          onChange={(e) => {
                            const value = e.target.value;
                            setFieldValue('pricePerUnit', value ? Number(value) : null);
                          }}
                          InputProps={{
                            startAdornment: <InputAdornment position="start">€</InputAdornment>
                          }}
                          sx={{
                            '& .MuiInputBase-input': {
                              textAlign: 'center'
                            }
                          }}
                          error={touched.pricePerUnit && errors.pricePerUnit}
                          helperText={touched.pricePerUnit && errors.pricePerUnit}
                        />
                      </Grid>
                      <Grid item xs={6}>
                        <FormControl fullWidth size="small" error={touched.unit && errors.unit}>
                          <InputLabel>{getLabel("_Unit")}</InputLabel>
                          <Select
                            value={safeStringValue(values.unit)}
                            label={getLabel("_Unit")}
                            onChange={(e) => setFieldValue('unit', e.target.value)}
                            sx={{
                              '& .MuiSelect-select': {
                                textAlign: 'center'
                              }
                            }}
                          >
                            {getUnits().map((unit) => (
                              <MenuItem key={unit.value} value={unit.value}>
                                {unit.title}
                              </MenuItem>
                            ))}
                          </Select>
                          {touched.unit && errors.unit && (
                            <FormHelperText>{errors.unit}</FormHelperText>
                          )}
                        </FormControl>
                      </Grid>
                      <Grid item xs={6}>
                        <TextField
                          fullWidth
                          size="small"
                          label={getLabel("_Quantity")}
                          type="number"
                          value={safeStringValue(values.quantity)}
                          onChange={(e) => {
                            const value = e.target.value;
                            setFieldValue('quantity', value ? Number(value) : null);
                          }}
                          sx={{
                            '& .MuiInputBase-input': {
                              textAlign: 'center'
                            }
                          }}
                          error={touched.quantity && errors.quantity}
                          helperText={touched.quantity && errors.quantity}
                        />
                      </Grid>
                      <Grid item xs={6}>
                        <TextField
                          fullWidth
                          size="small"
                          label={getLabel("_Extra-Cost-(Optional)")}
                          type="number"
                          value={safeStringValue(values.extraCost)}
                          onChange={(e) => {
                            const value = e.target.value;
                            setFieldValue('extraCost', value ? Number(value) : null);
                          }}
                          InputProps={{
                            startAdornment: <InputAdornment position="start">€</InputAdornment>
                          }}
                          sx={{
                            '& .MuiInputBase-input': {
                              textAlign: 'center'
                            }
                          }}
                          error={touched.extraCost && errors.extraCost}
                          helperText={touched.extraCost && errors.extraCost}
                        />
                      </Grid>
                      {/* Move % of element affected to a separate row */}
                      <Grid item xs={12}>
                        <TextField
                          fullWidth
                          size="small"
                          label={getLabel("_%-of-the-element-affected")}
                          type="number"
                          value={safeStringValue(values.elementAffected)}
                          onChange={(e) => {
                            const value = e.target.value;
                            setFieldValue('elementAffected', value ? Number(value) : null);
                          }}
                          InputProps={{ 
                            inputProps: { min: 1, max: 100 },
                            endAdornment: <InputAdornment position="end">%</InputAdornment>
                          }}
                          placeholder="Min: 1, Max: 100"
                          sx={{
                            '& .MuiInputBase-input': {
                              textAlign: 'center'
                            }
                          }}
                          error={touched.elementAffected && errors.elementAffected}
                          helperText={touched.elementAffected && errors.elementAffected}
                        />
                      </Grid>
                    </Grid>
                    
                    <Box sx={{ p: 1.5, bgcolor: 'success.50', borderRadius: 1, mx: 0.5, mt: 1.5 }}>
                      <Typography variant="h6" fontWeight="bold" color="success.dark">
                        {getIntlTranslation('_Total')}: €{calulateTotal(values, false) || 0}
                      </Typography>
                    </Box>

                    {/* Cost Threshold Warning - Same as old form */}
                    {values.pricePerUnit > 0 &&
                      Math.round(convertToDecimal(values, true) / 1000) * 1000 < 10000 && (
                        <Box sx={{ mx: 0.5, mt: 1 }}>
                          <Alert variant="filled" severity="warning">
                            {getIntlTranslation("_threshold")}
                          </Alert>
                        </Box>
                      )}

                    <Box sx={{ px: 0.5, mt: 1.5 }}>
                      <TextField
                        fullWidth
                        multiline
                        rows={4}
                        size="small"
                        label={getLabel("_Extra-Cost-Comments")}
                        value={localTextFields.extraCostComment}
                        onChange={createTextFieldHandler('extraCostComment')}
                        placeholder="Internal comments..."
                        sx={{
                          '& .MuiInputBase-input': {
                            textAlign: 'left'
                          }
                        }}
                        error={touched.extraCostComment && errors.extraCostComment}
                        helperText={touched.extraCostComment && errors.extraCostComment}
                      />
                    </Box>

                    {/* Priority Distribution Slider - Show when Multiple priority is selected and it's a cost defect */}
                    {multipleSelected && values.costDefect && (
                      <Box sx={{ mt: 2, p: 2, bgcolor: '#f5f5f5', borderRadius: 1, border: '1px solid #e0e0e0' }}>
                        <Typography variant="h6" sx={{ mb: 1.5, fontWeight: 'bold', color: '#d84315', textAlign: 'center', fontSize: '1.1rem' }}>
                          {getIntlTranslation('priority-distribution')}
                        </Typography>
                        
                        <Box sx={{ mb: 1, width: '100%' }}>
                          <MultiplePriority
                            useModern={true}
                            data={rangeData}
                            onChange={(range) => {
                              const shortPct = Number(range[0]);
                              const mediumPct = Number(range[1] - range[0]);
                              const longPct = Number(100 - range[1]);
                              
                              setFieldValue('percentageShortTermPriority', shortPct);
                              setFieldValue('percentageMidTermPriority', mediumPct);
                              setFieldValue('percentageLongTermPriority', longPct);
                              setFieldValue('multipleCost', {
                                short: shortPct / 100,
                                medium: mediumPct / 100,
                                long: longPct / 100,
                              });
                              
                              // Update local state immediately for display
                              setMultipleCost({
                                short: shortPct / 100,
                                medium: mediumPct / 100,
                                long: longPct / 100,
                              });
                            }}
                          />
                        </Box>
                        
                        {/* Cost Distribution Display & Total - Calculate once, use everywhere */}
                        {(() => {
                          // Calculate distributed costs once for both display sections
                          const rawTotal = parseFloat(convertToDecimal(values, false));
                          const totalRounded = Math.round(rawTotal / 1000) * 1000;
                          const distributedCosts = calculateDistributedCosts(
                            totalRounded,
                            multipleCost?.short || 0,
                            multipleCost?.medium || 0,
                            multipleCost?.long || 0
                          );
                          const sumOfDistributed = distributedCosts.short + distributedCosts.medium + distributedCosts.long;
                          
                          return (
                            <>
                              {/* Cost Distribution Display - Three colored boxes */}
                              <Box sx={{ display: 'flex', mt: 3, gap: 1, px: 1 }}>
                                <Box
                                  sx={{
                                    flex: 1,
                                    backgroundColor: '#e74c3c',
                                    py: 1.5,
                                    px: 1,
                                    textAlign: 'center',
                                    display: 'flex',
                                    alignItems: 'center',
                                    justifyContent: 'center',
                                    minHeight: '48px',
                                    borderRadius: 1,
                                    boxShadow: '0 2px 4px rgba(231, 76, 60, 0.2)',
                                    border: '1px solid rgba(231, 76, 60, 0.3)',
                                    minWidth: 0
                                  }}
                                >
                                  <Typography variant="body2" sx={{ color: 'white', fontWeight: 'bold', fontSize: '0.7rem', wordBreak: 'break-word' }}>
                                    {distributedCosts.short.toLocaleString('de-DE', { currency: 'EUR', style: 'currency' })}
                                  </Typography>
                                </Box>
                                <Box
                                  sx={{
                                    flex: 1,
                                    backgroundColor: '#f39c12',
                                    py: 1.5,
                                    px: 1,
                                    textAlign: 'center',
                                    display: 'flex',
                                    alignItems: 'center',
                                    justifyContent: 'center',
                                    minHeight: '48px',
                                    borderRadius: 1,
                                    boxShadow: '0 2px 4px rgba(243, 156, 18, 0.2)',
                                    border: '1px solid rgba(243, 156, 18, 0.3)',
                                    minWidth: 0
                                  }}
                                >
                                  <Typography variant="body2" sx={{ color: 'white', fontWeight: 'bold', fontSize: '0.7rem', wordBreak: 'break-word' }}>
                                    {distributedCosts.medium.toLocaleString('de-DE', { currency: 'EUR', style: 'currency' })}
                                  </Typography>
                                </Box>
                                <Box
                                  sx={{
                                    flex: 1,
                                    backgroundColor: '#27ae60',
                                    py: 1.5,
                                    px: 1,
                                    textAlign: 'center',
                                    display: 'flex',
                                    alignItems: 'center',
                                    justifyContent: 'center',
                                    minHeight: '48px',
                                    borderRadius: 1,
                                    boxShadow: '0 2px 4px rgba(39, 174, 96, 0.2)',
                                    border: '1px solid rgba(39, 174, 96, 0.3)',
                                    minWidth: 0
                                  }}
                                >
                                  <Typography variant="body2" sx={{ color: 'white', fontWeight: 'bold', fontSize: '0.7rem', wordBreak: 'break-word' }}>
                                    {distributedCosts.long.toLocaleString('de-DE', { currency: 'EUR', style: 'currency' })}
                                  </Typography>
                                </Box>
                              </Box>

                              {/* Total Cost Display - CAPEX estimate and original total */}
                              <Box sx={{ mt: 1.5, textAlign: 'center' }}>
                                {/* CAPEX estimate (rounded distributed sum) - shown first */}
                                <Typography variant="body2" color="text.secondary" sx={{ fontWeight: 'bold', fontSize: '0.9rem', display: 'block', mb: 0.5 }}>
                                  [{getIntlTranslation('_Cost-estimate-for-CAPEX')} {sumOfDistributed.toLocaleString('de-DE', { currency: 'EUR', style: 'currency' })}]
                                </Typography>
                                {/* Original total (user input) - shown below */}
                                <Typography variant="body1" sx={{ fontWeight: 'bold', color: '#244a9a', fontSize: '1.1rem' }}>
                                  {getIntlTranslation('_Total')} {rawTotal.toLocaleString('de-DE', { currency: 'EUR', style: 'currency' })}
                                </Typography>
                              </Box>
                            </>
                          );
                        })()}
                      </Box>
                    )}
                  </Stack>
                </CardContent>
              </Card>
            </Grid>
          )}
        </Grid>

        {/* Defect Images - Always visible */}
        <DefectImageUploaderWithGenerate
          key={`enhanced-form-images-${values.defectIdent || 'new'}`}
          row={stableRowForDefectImages}
          values={values}
          setFieldValue={setFieldValue}
          showGenerateButton={true}
          onSaveImages={onImageUploadData}
          subCategory={subCategory}
        />
      </Container>

      {/* Sticky Bottom Form Summary */}
      <FormSummary
        values={values}
        riskTypeOptions={riskTypeOptionsArray}
        defectTypeOptions={defectTypeOptions}
        priorityTypeOptions={priorityTypeOptions}
        projectLanguageCode={scope.projectLanguageCode}
        isElementOther={isElementOther}
        getElementTypeLabel={getElementTypeLabel}
      />
    </>
  );
}

export default EnhancedDefectForm;
